import {
  Box,
  Button,
  Col,
  CreateButton,
  Drawer,
  Dropdown,
  Flex,
  Loading,
  NavigateEditPageButton,
  NotAuthorized,
  NotFound,
  Row,
  Skeleton,
  Space,
  Tabs,
} from '@/components';
import { useLeavePageAlertState } from '@/components/Form/LeavePageAlert';
import {
  useCreateAttachment,
  useUploadFile,
} from '@/features/attachment/_hooks/attachment';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useIndicatorPermission } from '@/features/authentication/_hooks/permission';
import { getPinpointSheetType } from '@/features/comment/utils/pinpoint';
import { usePriorIdFromLineage } from '@/features/project/_hooks/project';
import {
  useProject,
  useUpdateFundInvestments,
  useUpdateInspectionEvaluation,
} from '@/features/project/_hooks/project';
import { isBefore2025 as isBefore2025Func } from '@/features/project/_utils/project';
import {
  DiscriminatedInspectionEvaluationModel,
  FundAdditionalAttributesModel,
} from '@/features/project/projects/_models/project';
import { useCreateActivity } from '@/features/project/projects/detail/activities/_hooks/activity';
import { useConsecutiveFiscalYears } from '@/features/project/projects/detail/activities/_hooks/consecutive';
import {
  useCreateRelatedMaterial,
  useRelatedMaterials,
  useUpdateRelatedMaterial,
} from '@/features/project/projects/detail/related-material/_hooks/related-material';
import { SITE_NAME as PS_SITE_NAME } from '@/features/public-site/utils';
import {
  useChangeBrowserTabTitleEffect,
  useElementOffsetHeight,
  useId,
} from '@/hooks';
import { useMessage } from '@/providers';
import { useFormDirtyStore } from '@/stores/form-dirty-store';
import {
  HEADER_ID_FOR_OFFSET_HEIGHT,
  PAGE_HEADER_PADDING,
  PS_HEADER_ID_FOR_OFFSET_HEIGHT,
  boolToString,
  isPS,
  logger,
} from '@/utils';
import { DownOutlined, TableOutlined } from '@ant-design/icons';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import type { RelatedMaterialFormSchema } from '../../_models/related-material';
import { AlertColumns } from '../_components/AlertColumns';
import { FiscalYearSelector } from '../_components/FiscalYearSelector';
import { InputPriorItemButton } from '../_components/InputPriorItemButton';
import { useProjectDetailDrawerState } from '../_components/ProjectDrawerStateProvider';
import { ActivitiesInformation } from '../activities/_components/ActivitiesInformation';
import { IndicatorFormDrawer } from '../activities/_components/IndicatorFormDrawer';
import { ActivityFormDrawer } from '../activities/_components/activities/ActivityFormDrawer';
import { RsProjectDetailedBreakdown } from '../detailed-breakdown/_components/rs/RsProjectDetailedBreakdown';
import { SsProjectDetailedBreakdown } from '../detailed-breakdown/_components/ss/SsProjectDetailedBreakdown';
import { ExecutionProgressInformation } from '../execution-progress/_components/ExecutionProgressInformation';
import { FundBusinessForInvestmentInformation } from '../fund-bussiness/_components/FundBusinessForInvestmentInformation';
import { FundBusinessInformation } from '../fund-bussiness/_components/FundBusinessInformation';
import { FundAdditionalAttributes } from '../fund/_components/FundAdditionalAttributes';
import { FundAdditionalAttributeFormsDrawer } from '../fund/_components/FundAdditionalAttributesFormDrawer';
import { FundInvestmentsForm } from '../fund/_components/FundInvestmentsForm';
import { FundInvestmentsInformation } from '../fund/_components/FundInvestmentsInformation';
import { OwnershipRatioForm } from '../fund/_components/OwnershipRatioForm';
import { OwnershipRatioInformation } from '../fund/_components/OwnershipRatioInformation';
import { FundAdditionalIncomeAndExpenditureDrawer } from '../income-and-expenditure/_components/fund-additional/FundAdditionalIncomeAndExpenditureDrawer';
import { FundAdditionalIncomeAndExpenditureInformation } from '../income-and-expenditure/_components/fund-additional/FundAdditionalIncomeAndExpenditureInformation';
import { UpsertPriorExecutionItemFormDrawer } from '../income-and-expenditure/_components/prior-execution/UpsertPriorExecutionItemFormDrawer';
import { UpsertPriorReserveBudgetItemFormDrawer } from '../income-and-expenditure/_components/prior-reserve-budget/UpsertPriorReserveBudgetItemFormDrawer';
import { InspectionEvaluation } from '../inspection-evaluation/_components/InspectionEvaluation';
import { InspectionEvaluationForm } from '../inspection-evaluation/_components/InspectionEvaluationForm';
import { KsStandardInspectionEvaluationAfter2025 } from '../inspection-evaluation/_components/KsStandardInspectionEvaluationAfter2025';
import { KsStandardInspectionEvaluationBefore2024 } from '../inspection-evaluation/_components/KsStandardInspectionEvaluationBefore2024';
import { SsInspectionEvaluation } from '../inspection-evaluation/_components/SsInspectionEvaluation';
import { PaymentTabContents } from '../payment/_components/PaymentTabContents';
import { ProjectInformation } from '../project-information/_components/ProjectInformation';
import { RecurringStats } from '../project-information/_components/RecurringStats';
import { RelatedMaterialFormDrawer } from '../related-material/_components/RelatedMaterialFormDrawer';
import { RelatedMaterialsInformation } from '../related-material/_components/RelatedMaterialsInformation';

const SkeletonContents: FC = () => {
  return (
    <Flex gap="16px 0" wrap="wrap" vertical>
      <Flex align="center" justify="space-between">
        <Flex gap={16} align="center">
          <Skeleton.Input active size="small" />
          <Skeleton.Input active />
        </Flex>
        <Skeleton.Input active />
      </Flex>
      <Skeleton.Node
        active
        style={{
          width: '100%',
          height: 464,
        }}
      >
        <TableOutlined
          style={{
            fontSize: 120,
            opacity: 0.1,
          }}
        />
      </Skeleton.Node>
    </Flex>
  );
};

export const ProjectDetailsPage: FC = () => {
  const { t } = useTranslation();
  const { id } = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const project = useProject(id);
  const relatedMaterials = useRelatedMaterials(id);
  const navigate = useNavigate();
  const isBefore2025 = isBefore2025Func(project.data);

  const consecutiveFiscalYears = useConsecutiveFiscalYears({ id });
  const priorId = usePriorIdFromLineage({
    params: {
      fiscalYear: project.data?.fiscalYear,
      lineageId: project.data?.lineageId ?? '',
    },
  });
  const priorProject = useProject(priorId.data?.priorProjectId ?? '');

  const editTargetRelatedMaterial = useMemo(
    () => relatedMaterials.data?.[0],
    [relatedMaterials],
  );
  const relatedMaterialImages = useMemo(
    () =>
      relatedMaterials.data?.flatMap(
        (relatedMaterial) => relatedMaterial.attachments,
      ),
    [relatedMaterials],
  );
  const additionalExplanation = useMemo(
    () => relatedMaterials.data?.[0]?.additionalExplanation,
    [relatedMaterials],
  );
  const { leavePageAlertReference } = useLeavePageAlertState();
  const { isDirty } = useFormDirtyStore();

  const { mutateAsync: createActivityAsync } = useCreateActivity();
  const { mutateAsync: updateFundInvestments } = useUpdateFundInvestments();
  const { mutateAsync: updateInspectionEvaluationAsync } =
    useUpdateInspectionEvaluation();
  const { mutateAsync: createRelatedMaterialsAsync } =
    useCreateRelatedMaterial();
  const { mutateAsync: updateRelatedMaterialsAsync } =
    useUpdateRelatedMaterial();
  const { mutateAsync: createAttachmentAsync } = useCreateAttachment();
  const { mutateAsync: uploadFileAsync } = useUploadFile();
  const { openMessage } = useMessage();
  const viewIndicatorPermission = useIndicatorPermission();
  const editIndicatorPermission = useIndicatorPermission(true);
  useChangeBrowserTabTitleEffect({
    title: project.data?.name,
    fallback: isPS() ? PS_SITE_NAME : undefined,
  });

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  const uploadAttachment = useCallback(
    async (file: File | null) => {
      try {
        if (!file) {
          return;
        }

        const attachment = await createAttachmentAsync({
          fileName: file.name,
          fileContentType: file.type,
        });
        await uploadFileAsync({
          presignedUrl: attachment.presignedUrl,
          file,
        });
        return attachment.id;
      } catch {
        await openMessage('error', t('error-message-with-file'));
      }
    },
    [createAttachmentAsync, uploadFileAsync, openMessage, t],
  );

  const onSubmitRelatedMaterial = async ({
    values,
    file,
  }: {
    values: z.infer<typeof RelatedMaterialFormSchema>;
    file: File | null;
  }) => {
    try {
      const uploadedAttachmentId = await uploadAttachment(file);

      const getAttachmentIds = (
        attachments: z.infer<typeof RelatedMaterialFormSchema>['attachments'],
        uploadedAttachmentId: string | undefined,
      ) => {
        if (uploadedAttachmentId) {
          return [uploadedAttachmentId];
        }
        return attachments.map((attachment) => attachment.id);
      };

      editTargetRelatedMaterial
        ? await updateRelatedMaterialsAsync({
            id: editTargetRelatedMaterial.id,
            ...values,
            attachmentIds: getAttachmentIds(
              values.attachments,
              uploadedAttachmentId,
            ),
          })
        : await createRelatedMaterialsAsync({
            ...values,
            attachmentIds: getAttachmentIds(
              values.attachments,
              uploadedAttachmentId,
            ),
          });

      await Promise.all([
        openMessage('success', t('message-update-completed')),
        handleClose(),
      ]);
    } catch {
      await openMessage('error', t('message-update-failed'));
    }
  };

  const activeKey = searchParams.get('activeKey') ?? 'basic-information';
  const currentParams = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams],
  );

  useEffect(() => {
    if (activeKey === 'ownership-ratio' && isBefore2025) {
      setSearchParams(
        { ...currentParams, activeKey: 'inspection-evaluation' },
        { replace: true },
      );
    }
  }, [activeKey, currentParams, isBefore2025, setSearchParams]);

  const showNavigateToReportPageButton = useMemo(() => {
    // TODO: Refactoring Logic
    return (
      !project.isPending &&
      project.data &&
      (project.data.sheetType === 'RS' ||
        project.data.sheetType === 'SS' ||
        project.data.fundAdditionalAttributes?.fundSheetType === 'standard') &&
      !project.data.skipAdministrativeReformTeamEvaluation
    );
  }, [project.data, project.isPending]);

  const showInputPriorButton = useMemo(() => {
    return !!(
      project.data?.fiscalYear &&
      consecutiveFiscalYears.data?.find(
        (d) => d.fiscalYear === (project.data?.fiscalYear ?? 0) - 1,
      )
    );
  }, [consecutiveFiscalYears.data, project.data?.fiscalYear]);

  const parsedFundAdditionalAttributes =
    FundAdditionalAttributesModel.safeParse(
      project.data?.fundAdditionalAttributes,
    );

  const initialFundInvestmentsFormValues = useMemo(() => {
    if (
      !parsedFundAdditionalAttributes.success ||
      !parsedFundAdditionalAttributes.data.investments
    ) {
      return {};
    }
    if (
      isBefore2025 ||
      !priorProject.data?.fundAdditionalAttributes?.investments
        ?.annualInvestmentsFromTheGovernment
    ) {
      return parsedFundAdditionalAttributes.data.investments;
    }
    // NOTE: 2025年度以降の事業で、前年度で入力されている場合は、その値を自動反映する
    return {
      ...parsedFundAdditionalAttributes.data.investments,
      annualInvestmentsFromTheGovernment: [
        ...(
          parsedFundAdditionalAttributes.data.investments
            .annualInvestmentsFromTheGovernment ?? [0]
        ).map((value, i) =>
          i === 0
            ? priorProject.data?.fundAdditionalAttributes?.investments
                ?.annualInvestmentsFromTheGovernment?.[1]
            : value,
        ),
      ],
    };
  }, [isBefore2025, parsedFundAdditionalAttributes, priorProject.data]);

  const headerHeight = useElementOffsetHeight({
    targetId: isPS()
      ? PS_HEADER_ID_FOR_OFFSET_HEIGHT
      : HEADER_ID_FOR_OFFSET_HEIGHT,
  });

  if (project.isError) {
    if ((project.error as AxiosError).response?.status === 403) {
      return (
        <NotAuthorized
          message={t('error-message-not-project-authorized')}
          height={`calc(100vh - ${headerHeight}px - ${PAGE_HEADER_PADDING * 2}px)`}
          backRoute="/rs/project"
        />
      );
    }
    logger.error('failed to fetch project', project.error);
    return <NotFound />;
  }

  if (project.isPending || !project.data) {
    return isPS() ? <SkeletonContents /> : <Loading />;
  }

  const parsed = DiscriminatedInspectionEvaluationModel.safeParse({
    ...project.data,
  });

  const isReviewSkipped =
    project.data.sheetType === 'RS' &&
    project.data.skipAdministrativeReformTeamEvaluation;

  const BaseTabItemWrapper = ({ children }: { children: ReactNode }) => {
    return <Box maxWidth={1792}>{children}</Box>;
  };

  const pinpointSheetType = getPinpointSheetType(
    project.data.sheetType,
    project.data.fundAdditionalAttributes?.fundSheetType,
  );

  const fundSheetType =
    project.data.sheetType === 'KS' &&
    project.data.fundAdditionalAttributes?.fundSheetType;

  const baseTabItems = [
    {
      key: 'basic-information',
      label: t('basic-information'),
      children: (
        <BaseTabItemWrapper>
          <ProjectInformation {...project.data} />
        </BaseTabItemWrapper>
      ),
    },
    ...(project.data.sheetType === 'KS' && fundSheetType !== 'investment'
      ? [
          {
            key: 'fund-additional-attributes',
            label: isBefore2025
              ? t('fund-additional-attributes-before-2025')
              : t('fund-additional-attributes'),
            children: <FundAdditionalAttributes {...project.data} />,
          },
        ]
      : []),
    // TODO: 基金の場合はbudget-amountsタブをincome-and-expenseタブにする対応
    ...(fundSheetType === 'investment'
      ? [
          {
            key: 'fund-investments',
            label: t('investment'),
            children: (
              <FundInvestmentsInformation
                priorProjectId={priorId.data?.priorProjectId ?? ''}
                fiscalYear={project.data.fiscalYear}
                fundAdditionalAttributes={project.data.fundAdditionalAttributes}
              />
            ),
          },
        ]
      : []),
    ...(project.data.sheetType !== 'KS'
      ? [
          {
            key: 'detailed-breakdown',
            label: t('budget-and-execution'),
            children:
              project.data.sheetType === 'RS' ? (
                <RsProjectDetailedBreakdown {...project.data} />
              ) : (
                <SsProjectDetailedBreakdown />
              ),
          },
        ]
      : []),
    ...(fundSheetType === 'standard' && isBefore2025
      ? [
          {
            key: 'income-and-expenditure',
            label: t('income-and-expenditure-title'),
            children: (
              <FundAdditionalIncomeAndExpenditureInformation
                {...project.data}
              />
            ),
          },
        ]
      : []),
    ...(viewIndicatorPermission.data &&
    !isReviewSkipped &&
    fundSheetType !== 'investment'
      ? [
          {
            key: 'activities',
            label: t('way-to-effect'),
            children: (
              <ActivitiesInformation editable={!project.data.isLocked} />
            ),
          },
        ]
      : []),
    ...(fundSheetType === 'standard' && !isBefore2025
      ? [
          {
            key: 'income-and-expenditure',
            label: t('income-and-expenditure-title'),
            children: (
              <FundAdditionalIncomeAndExpenditureInformation
                {...project.data}
              />
            ),
          },
        ]
      : []),
    ...(fundSheetType === 'standard' || fundSheetType === 'investment'
      ? [
          {
            key: 'fund-business',
            label: t('fund-business'),
            children:
              fundSheetType === 'standard' ? (
                <FundBusinessInformation
                  fiscalYear={project.data.fiscalYear}
                  editable={!project.data.isLocked}
                />
              ) : (
                <FundBusinessForInvestmentInformation
                  fiscalYear={project.data.fiscalYear}
                  editable={!project.data.isLocked}
                />
              ),
          },
        ]
      : []),
    ...(fundSheetType === 'standard' && !isBefore2025
      ? [
          {
            key: 'ownership-ratio',
            label: t('ownership-ratio'),
            children: <OwnershipRatioInformation {...project.data} />,
          },
        ]
      : []),
    ...(fundSheetType === 'local-government'
      ? [
          {
            key: 'execution-progress',
            label: t('execution-progress'),
            children: (
              <ExecutionProgressInformation
                fiscalYear={project.data.fiscalYear}
                name={project.data.name}
                fundBusinessForLocalGovernmentSummary={
                  project.data.fundBusinessForLocalGovernmentSummary
                }
                fundAdditionalAttributes={project.data.fundAdditionalAttributes}
                editable={!project.data.isLocked}
              />
            ),
          },
        ]
      : []),
    ...((project.data.sheetType !== 'KS' && !isReviewSkipped) ||
    fundSheetType === 'standard'
      ? [
          ...(project.data.sheetType === 'SS'
            ? [
                {
                  key: 'inspection-evaluation',
                  label: t('inspection-evaluation'),
                  children: <SsInspectionEvaluation {...project.data} />,
                },
              ]
            : fundSheetType === 'standard'
              ? [
                  {
                    key: 'inspection-evaluation',
                    label: t('inspection-evaluation'),
                    children: isBefore2025 ? (
                      <KsStandardInspectionEvaluationBefore2024
                        inspectionEvaluation={project.data.inspectionEvaluation}
                      />
                    ) : (
                      <KsStandardInspectionEvaluationAfter2025
                        inspectionEvaluation={project.data.inspectionEvaluation}
                      />
                    ),
                  },
                ]
              : [
                  {
                    key: 'inspection-evaluation',
                    label: t('inspection-evaluation'),
                    children: <InspectionEvaluation {...project.data} />,
                  },
                ]),
          {
            key: 'payment',
            label: t('payment'),
            children: (
              <PaymentTabContents
                paymentIndirectCosts={project.data.paymentIndirectCosts}
              />
            ),
          },
        ]
      : []),
    {
      key: 'related-material',
      label: t('related-material'),
      children: (
        <RelatedMaterialsInformation
          additionalExplanation={additionalExplanation}
          images={relatedMaterialImages}
          editable={!project.data.isLocked}
          pinpointSheetType={pinpointSheetType}
          onClickUpload={() => {
            setDrawerState({
              open: true,
              type: 'related-material',
              isEditing: !!editTargetRelatedMaterial,
            });
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Flex gap={16} vertical>
            <Flex justify="space-between">
              <FiscalYearSelector fiscalYear={project.data.fiscalYear} />
              <Flex gap={8}>
                {showNavigateToReportPageButton && (
                  <Button
                    onClick={() => {
                      navigate(
                        isPS()
                          ? `${location.pathname.replace('/public-site', '')}/report`
                          : `${location.pathname}/report`,
                      );
                    }}
                  >
                    {isPS() ? t('preview-print') : t('preview-report-projects')}
                  </Button>
                )}
                {showInputPriorButton && (
                  <PublishedProjectMutableGuard>
                    <InputPriorItemButton />
                  </PublishedProjectMutableGuard>
                )}
              </Flex>
            </Flex>
            {/* TODO: The feature will be opened at 7/1 */}
            {project.data.sheetType === 'RS' && <RecurringStats />}
          </Flex>
        </Col>

        <AlertColumns
          isDiscarded={project.data.isDiscarded}
          isLocked={project.data.isLocked}
          isPublished={project.data.isPublished}
          publishedAt={project.data.publishedAt}
          lockedAt={project.data.lockedAt}
        />

        <Col span={24}>
          <Tabs
            activeKey={activeKey}
            destroyInactiveTabPane
            tabBarExtraContent={
              <Space>
                {activeKey === 'basic-information' && (
                  <ProjectResourceGuard onlyEdit>
                    <PublishedProjectMutableGuard>
                      {!project.data.isLocked && <NavigateEditPageButton />}
                    </PublishedProjectMutableGuard>
                  </ProjectResourceGuard>
                )}
                {activeKey === 'fund-business' && (
                  <ProjectResourceGuard onlyEdit>
                    <PublishedProjectMutableGuard>
                      {!project.data.isLocked && (
                        <CreateButton
                          onCreate={() => {
                            invariant(fundSheetType, t('unreachable-code'));

                            setDrawerState({
                              open: true,
                              isEditing: false,
                              type: 'fund-business',
                              fundSheetType,
                            });
                          }}
                        />
                      )}
                    </PublishedProjectMutableGuard>
                  </ProjectResourceGuard>
                )}
                {activeKey === 'fund-additional-attributes' &&
                  project.data.sheetType === 'KS' && (
                    <ProjectResourceGuard onlyEdit>
                      <PublishedProjectMutableGuard>
                        {!project.data.isLocked && (
                          <Button
                            type="primary"
                            onClick={() => {
                              setDrawerState({
                                type: 'fund-additional-attributes',
                                open: true,
                              });
                            }}
                          >
                            {t('edit')}
                          </Button>
                        )}
                      </PublishedProjectMutableGuard>
                    </ProjectResourceGuard>
                  )}
                {activeKey === 'fund-investments' &&
                  project.data.sheetType === 'KS' && (
                    <ProjectResourceGuard onlyEdit>
                      <PublishedProjectMutableGuard>
                        {!project.data.isLocked && (
                          <Button
                            type="primary"
                            onClick={() => {
                              setDrawerState({
                                open: true,
                                type: 'fund-investments',
                              });
                            }}
                          >
                            {t('edit')}
                          </Button>
                        )}
                      </PublishedProjectMutableGuard>
                    </ProjectResourceGuard>
                  )}
                {activeKey === 'income-and-expenditure' && fundSheetType && (
                  <ProjectResourceGuard onlyEdit>
                    <PublishedProjectMutableGuard>
                      {!project.data.isLocked && (
                        <Dropdown
                          menu={{
                            items: [
                              ...(project.data
                                .fundAdditionalIncomeAndExpenditure?.prior
                                ? [
                                    {
                                      key: 'update-previous-fiscal-year-performance',
                                      label: t(
                                        'update-previous-fiscal-year-performance',
                                      ),
                                    },
                                  ]
                                : []),
                              {
                                key: 'update-current-fiscal-year-estimation',
                                label: t(
                                  'update-current-fiscal-year-estimation',
                                ),
                              },
                              {
                                key: 'update-next-fiscal-year-estimation',
                                label: t('update-next-fiscal-year-estimation'),
                              },
                            ],
                            onClick: (e) => {
                              switch (e.key) {
                                case 'update-previous-fiscal-year-performance':
                                  setDrawerState({
                                    open: true,
                                    type: 'fund-additional-income-and-expenditure',
                                    projectId: id,
                                  });
                                  break;
                                case 'update-current-fiscal-year-estimation':
                                  setDrawerState({
                                    open: true,
                                    type: 'estimated-fund-additional-income-and-expenditure',
                                    projectId: id,
                                  });
                                  break;
                                case 'update-next-fiscal-year-estimation':
                                  setDrawerState({
                                    open: true,
                                    type: 'estimated-fund-additional-income-and-expenditure-next-year',
                                    projectId: id,
                                  });
                                  break;
                                default:
                              }
                            },
                          }}
                        >
                          <Button type="primary">
                            {t('edit-estimate')}
                            <DownOutlined aria-label="" />
                          </Button>
                        </Dropdown>
                      )}
                    </PublishedProjectMutableGuard>
                  </ProjectResourceGuard>
                )}
                {((activeKey === 'ownership-ratio' && !isBefore2025) ||
                  (activeKey === 'inspection-evaluation' &&
                    fundSheetType === 'standard' &&
                    isBefore2025)) && (
                  <ProjectResourceGuard onlyEdit>
                    <PublishedProjectMutableGuard>
                      {!project.data.isLocked && (
                        <Button
                          type="primary"
                          onClick={() => {
                            setDrawerState({
                              open: true,
                              type: 'ownership-ratio',
                            });
                          }}
                        >
                          {t('edit-ownership-ratio')}
                        </Button>
                      )}
                    </PublishedProjectMutableGuard>
                  </ProjectResourceGuard>
                )}
                {activeKey === 'inspection-evaluation' && (
                  <ProjectResourceGuard onlyEdit>
                    <PublishedProjectMutableGuard>
                      {!project.data.isLocked && (
                        <Button
                          type="primary"
                          onClick={() => {
                            setDrawerState({
                              open: true,
                              type: 'inspection-evaluation',
                            });
                          }}
                        >
                          {t('edit-inspection-evaluation')}
                        </Button>
                      )}
                    </PublishedProjectMutableGuard>
                  </ProjectResourceGuard>
                )}
                {activeKey === 'related-material' && (
                  <ProjectResourceGuard onlyEdit>
                    <PublishedProjectMutableGuard>
                      {!project.data.isLocked && (
                        <Button
                          type="primary"
                          onClick={() => {
                            setDrawerState({
                              open: true,
                              type: 'related-material',
                              isEditing: !!editTargetRelatedMaterial,
                            });
                          }}
                        >
                          {t('edit-related-material')}
                        </Button>
                      )}
                    </PublishedProjectMutableGuard>
                  </ProjectResourceGuard>
                )}
              </Space>
            }
            items={baseTabItems}
            onChange={(activeKey) => {
              setSearchParams(
                { ...currentParams, activeKey },
                { replace: true },
              );
              leavePageAlertReference.current.afterAllowAction = () => {
                setDrawerState({ open: false });
              };
              if (!isDirty) {
                setDrawerState({ open: false });
              }
            }}
          />
        </Col>
      </Row>

      {editIndicatorPermission.data && (
        <ActivityFormDrawer
          open={
            drawerState.open &&
            drawerState.type === 'activity' &&
            !drawerState.isEditing
          }
          onSubmit={async (values) => {
            await createActivityAsync({
              projectId: id,
              ...values,
            });

            await Promise.all([
              openMessage('success', t('message-create-completed')),
              handleClose(),
            ]);
          }}
          onClose={handleClose}
        />
      )}

      <FundAdditionalAttributeFormsDrawer />

      <Drawer
        open={drawerState.open && drawerState.type === 'fund-investments'}
        onClose={handleClose}
        destroyOnClose
      >
        <FundInvestmentsForm
          initialValues={initialFundInvestmentsFormValues}
          fiscalYear={project.data.fiscalYear}
          onSubmit={async (values) => {
            invariant(
              parsedFundAdditionalAttributes.success,
              t('unreachable-code'),
            );
            await updateFundInvestments({
              id,
              ...values,
            });
            await Promise.all([
              openMessage('success', t('message-update-completed')),
              handleClose(),
            ]);
          }}
        />
      </Drawer>

      {project.data.fundAdditionalIncomeAndExpenditure && (
        <FundAdditionalIncomeAndExpenditureDrawer
          fiscalYear={project.data.fiscalYear}
          fundAdditionalIncomeAndExpenditure={
            project.data.fundAdditionalIncomeAndExpenditure
          }
        />
      )}

      {fundSheetType === 'standard' && (
        <Drawer
          size="large"
          open={drawerState.open && drawerState.type === 'ownership-ratio'}
          onClose={handleClose}
          destroyOnClose
        >
          <OwnershipRatioForm
            fiscalYear={project.data.fiscalYear}
            initialValues={{
              ...(parsed.success &&
                parsed.data.sheetType === 'KS' && {
                  ...project.data.inspectionEvaluation,
                  inspectionAndImprovementByAdministrativeJurisdiction: {
                    ...parsed.data.inspectionEvaluation
                      .inspectionAndImprovementByAdministrativeJurisdiction,
                  },
                  ownershipRatio: {
                    ...parsed.data.inspectionEvaluation.ownershipRatio,
                    operationType:
                      parsed.data.inspectionEvaluation.ownershipRatio
                        ?.operationType ?? 'auto',
                  },
                  lowUsedFundInvestigation: {
                    ...parsed.data.inspectionEvaluation
                      .lowUsedFundInvestigation,
                    hasCeasedOperations:
                      boolToString(
                        parsed.data.inspectionEvaluation
                          .lowUsedFundInvestigation?.hasCeasedOperations,
                      ) ?? undefined,
                    hasNoResultRecent:
                      boolToString(
                        parsed.data.inspectionEvaluation
                          .lowUsedFundInvestigation?.hasNoResultRecent,
                      ) ?? undefined,
                    hasFundThatLostPurpose:
                      boolToString(
                        parsed.data.inspectionEvaluation
                          .lowUsedFundInvestigation?.hasFundThatLostPurpose,
                      ) ?? undefined,
                    hasExceedOneSignificantly:
                      boolToString(
                        parsed.data.inspectionEvaluation
                          .lowUsedFundInvestigation?.hasExceedOneSignificantly,
                      ) ?? undefined,
                    hasDeemedUnlikelyToBeUsed:
                      boolToString(
                        parsed.data.inspectionEvaluation
                          .lowUsedFundInvestigation?.hasDeemedUnlikelyToBeUsed,
                      ) ?? undefined,
                  },
                }),
            }}
            onSubmit={async (values) => {
              invariant(parsed.success, t('unreachable-code'));

              await updateInspectionEvaluationAsync({
                id,
                sheetType: parsed.data.sheetType,
                ...values,
              });

              await Promise.all([
                openMessage('success', t('message-update-completed')),
                (async () => {
                  setDrawerState({ open: false });
                })(),
              ]);
            }}
          />
        </Drawer>
      )}

      <Drawer
        size="large"
        open={drawerState.open && drawerState.type === 'inspection-evaluation'}
        onClose={handleClose}
        destroyOnClose
      >
        <InspectionEvaluationForm
          fiscalYear={project.data.fiscalYear}
          type={project.data.sheetType}
          initialValues={{
            ...(parsed.success &&
              parsed.data.sheetType === 'RS' && {
                ...project.data.inspectionEvaluation,
                inspectionAndImprovementByAdministrativeJurisdiction: {
                  ...parsed.data.inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction,
                },
                inspectionByExternalExpert: {
                  ...parsed.data.inspectionEvaluation
                    .inspectionByExternalExpert,
                  lastImplementedFiscalYear: parsed.data.inspectionEvaluation
                    .inspectionByExternalExpert?.lastImplementedFiscalYear
                    ? dayjs().year(
                        parsed.data.inspectionEvaluation
                          .inspectionByExternalExpert.lastImplementedFiscalYear,
                      )
                    : undefined,
                },
                summariesAndResponses:
                  parsed.data.inspectionEvaluation.summariesAndResponses?.map(
                    (summaryAndResponse) => {
                      return {
                        ...summaryAndResponse,
                        fiscalYear: summaryAndResponse.fiscalYear
                          ? dayjs().year(summaryAndResponse.fiscalYear)
                          : undefined,
                      };
                    },
                  ),
                otherIndicatedItemsAndResponses:
                  parsed.data.inspectionEvaluation.otherIndicatedItemsAndResponses?.map(
                    (otherIndicatedItemAndResponse) => {
                      return {
                        ...otherIndicatedItemAndResponse,
                        fiscalYear: otherIndicatedItemAndResponse.fiscalYear
                          ? dayjs().year(
                              otherIndicatedItemAndResponse.fiscalYear,
                            )
                          : undefined,
                      };
                    },
                  ),
              }),
            ...(parsed.success &&
              parsed.data.sheetType === 'SS' && {
                ...project.data.inspectionEvaluation,
                inspectionAndImprovementByAdministrativeJurisdiction: {
                  ...parsed.data.inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction,
                },
              }),
            ...(parsed.success &&
              parsed.data.sheetType === 'KS' && {
                ...project.data.inspectionEvaluation,
                inspectionAndImprovementByAdministrativeJurisdiction: {
                  ...parsed.data.inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction,
                },
                ownershipRatio: {
                  ...parsed.data.inspectionEvaluation.ownershipRatio,
                  operationType:
                    parsed.data.inspectionEvaluation.ownershipRatio
                      ?.operationType ?? 'auto',
                },
                lowUsedFundInvestigation: {
                  ...parsed.data.inspectionEvaluation.lowUsedFundInvestigation,
                  hasCeasedOperations: boolToString(
                    parsed.data.inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasCeasedOperations,
                  ),
                  hasNoResultRecent: boolToString(
                    parsed.data.inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasNoResultRecent,
                  ),
                  hasFundThatLostPurpose: boolToString(
                    parsed.data.inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasFundThatLostPurpose,
                  ),
                  hasExceedOneSignificantly: boolToString(
                    parsed.data.inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasExceedOneSignificantly,
                  ),
                  hasDeemedUnlikelyToBeUsed: boolToString(
                    parsed.data.inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasDeemedUnlikelyToBeUsed,
                  ),
                },
              }),
          }}
          onSubmit={async (values) => {
            invariant(parsed.success, t('unreachable-code'));

            await updateInspectionEvaluationAsync({
              id,
              sheetType: parsed.data.sheetType,
              ...values,
            });

            await Promise.all([
              openMessage('success', t('message-update-completed')),
              (async () => {
                setDrawerState({ open: false });
              })(),
            ]);
          }}
        />
      </Drawer>

      {viewIndicatorPermission.data && (
        <IndicatorFormDrawer
          projectId={id}
          fiscalYear={project.data.fiscalYear}
        />
      )}

      {priorId.data?.priorProjectId && (
        <>
          <UpsertPriorExecutionItemFormDrawer
            priorProjectId={priorId.data.priorProjectId}
          />
          <UpsertPriorReserveBudgetItemFormDrawer
            priorProjectId={priorId.data.priorProjectId}
          />
        </>
      )}

      <RelatedMaterialFormDrawer
        projectId={project.data.id}
        relatedMaterial={editTargetRelatedMaterial}
        onSubmitRelatedMaterial={onSubmitRelatedMaterial}
      />
    </>
  );
};
